import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import BrandHeader from "../components/BrandHeader";
import ProgressStepper, { RESPONDENT_STEPS } from "../components/ProgressStepper";
import Confetti from "../components/Confetti";
import ChangePasswordDialog from "../components/ChangePasswordDialog";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Label } from "../components/ui/label";
import { useAuth } from "../lib/auth";
import api, { API } from "../lib/api";
import { toast } from "sonner";
import {
  ArrowRight, CheckCircle2, Download, Brain,
  ArrowLeft, HeartPulse, Beaker, PartyPopper, Sparkles, KeyRound,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";

const SECTION_META = {
  MENTAL_HEALTH: {
    label: "Mental Health",
    tagline: "Depression, anxiety, stress and mood",
    icon: HeartPulse,
  },
  BRAIN_HEALTH: {
    label: "Brain Health",
    tagline: "Cognitive & neurodegenerative screens",
    icon: Brain,
  },
  ADDICTIONS: {
    label: "Addictions",
    tagline: "Alcohol and drug use screens",
    icon: Beaker,
  },
};
const SECTION_ORDER = ["MENTAL_HEALTH", "BRAIN_HEALTH", "ADDICTIONS"];

// Soft celebration modal fired when a respondent completes every instrument
// in a section — the win we didn't want to make them wait until /results for.
// When ALL remaining sections are done, we escalate to a full-viewport confetti
// burst and auto-navigate to /results.
function SectionCelebration({ sectionKey, sectionStats, onClose, onContinue, onPickAnother }) {
  const open = !!sectionKey;
  const meta = sectionKey ? SECTION_META[sectionKey] : null;
  const stats = sectionKey ? (sectionStats[sectionKey] || { total: 0, done: 0 }) : null;
  const remainingSections = SECTION_ORDER.filter((k) => {
    if (k === sectionKey) return false;
    const s = sectionStats[k] || { total: 0, done: 0 };
    return s.total > 0 && s.done < s.total;
  });
  const isFinale = open && remainingSections.length === 0;

  // Auto-route to the main hub after the confetti finale — respondents land on
  // /questionnaires (section picker + voice-MoCA banner + View-results button)
  // so every option is one tap away, and won't feel forced into the results view.
  useEffect(() => {
    if (!isFinale) return;
    const t = setTimeout(() => { onContinue(); }, 2600);
    return () => clearTimeout(t);
  }, [isFinale, onContinue]);

  return (
    <>
      <Confetti active={isFinale} />
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-md" data-testid="section-celebration-dialog">
          <DialogHeader>
            <div className="relative flex items-center justify-center mb-2">
              <span className="absolute -top-3 -left-4 text-lg opacity-70 mano-celebrate-sparkle" aria-hidden>✨</span>
              <span className="absolute -top-4 right-0 text-xl opacity-70 mano-celebrate-sparkle" style={{ animationDelay: "180ms" }} aria-hidden>🎉</span>
              <span className="absolute top-2 right-10 text-base opacity-70 mano-celebrate-sparkle" style={{ animationDelay: "340ms" }} aria-hidden>✨</span>
              <div className={"inline-flex items-center justify-center rounded-full bg-primary/10 text-primary " +
                              (isFinale ? "h-20 w-20" : "h-16 w-16")}>
                <PartyPopper size={isFinale ? 34 : 28} />
              </div>
            </div>
            <DialogTitle className="text-center font-display text-2xl leading-tight" data-testid="section-celebration-title">
              {isFinale ? "All sections complete!" : `${meta?.label} section complete!`}
            </DialogTitle>
            <DialogDescription className="text-center" data-testid="section-celebration-body">
              {isFinale ? (
                <>You finished every questionnaire across Mental Health, Brain Health and Addictions.
                  Taking you back to your main hub — every option is one tap away.</>
              ) : (
                <>
                  You finished all {stats?.total} {stats?.total === 1 ? "questionnaire" : "questionnaires"} in this section.
                  {remainingSections.length > 0 && (
                    <> Nice work — you can jump straight to your results, or keep going with{" "}
                      <b>{remainingSections.map((k) => SECTION_META[k]?.label).join(" and ")}</b>.
                    </>
                  )}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            {!isFinale && remainingSections.length > 0 && (
              <Button variant="outline" onClick={onPickAnother}
                      className="rounded-full w-full sm:w-auto"
                      data-testid="section-celebration-pick-another">
                Pick another section
              </Button>
            )}
            <Button onClick={onContinue}
                    className="rounded-full w-full sm:w-auto mano-lift"
                    data-testid="section-celebration-continue">
              <Sparkles size={14} /> {isFinale ? "Back to main hub" : "Continue to results"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function Questionnaires() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const deepInstrument = params.get("instrument"); // /q/:id → ?instrument=<id>
  const [instruments, setInstruments] = useState([]);
  const [instIdx, setInstIdx] = useState(0);
  const [qIdx, setQIdx] = useState(0);
  const [responses, setResponses] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState({}); // instrument_id: true
  const [activeSection, setActiveSection] = useState(null); // null => picker
  const [celebrating, setCelebrating] = useState(null); // sectionKey when just finished a section
  const [changePwOpen, setChangePwOpen] = useState(false);
  // { instrument_id, title, score, band, label } — set after every submit so
  // the picker can show a per-questionnaire result banner without navigating
  // to /results. Cleared once the respondent moves on.
  const [justCompleted, setJustCompleted] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      // Preserve deep-link across login
      if (deepInstrument) {
        nav(`/login?next=${encodeURIComponent(`/q/${deepInstrument}`)}`, { replace: true });
      } else {
        nav("/login");
      }
      return;
    }
    // Gate: force the 30-second voice intro before the picker. Skip check
    // when we're arriving via a deep-link — the user came from social media
    // for a specific questionnaire and we don't want to derail them.
    if (!deepInstrument) {
      api.get("/voice-intro/status")
        .then((r) => { if (!r.data?.completed) nav("/voice-intro", { replace: true }); })
        .catch(() => {}); // fail-open so backend hiccups don't lock users out
    }
    Promise.all([
      api.get("/instruments"),
      api.get("/responses/my/completed").catch(() => ({ data: [] })),
    ]).then(([instRes, doneRes]) => {
      setInstruments(instRes.data);
      const done = {};
      for (const r of doneRes.data || []) done[r.instrument_id] = true;
      setCompleted(done);
      // If we arrived via /q/:instrumentId, auto-enter the matching section.
      if (deepInstrument) {
        const match = instRes.data.find((i) => i.instrument_id === deepInstrument);
        if (match) {
          setActiveSection(match.section);
          const sectionList = instRes.data.filter((i) => i.section === match.section);
          const idx = sectionList.findIndex((i) => i.instrument_id === deepInstrument);
          setInstIdx(Math.max(0, idx));
        } else {
          toast.error("That questionnaire is not available right now");
        }
      }
    }).catch(() => toast.error("Could not load questionnaires"));
  }, [user, loading, nav, deepInstrument]);

  // Instruments filtered to the section the respondent picked (if any).
  const sectionInstruments = useMemo(
    () => (activeSection ? instruments.filter((i) => i.section === activeSection) : []),
    [activeSection, instruments],
  );

  // Per-section counts for the picker.
  const sectionStats = useMemo(() => {
    const stats = {};
    for (const key of SECTION_ORDER) stats[key] = { total: 0, done: 0 };
    for (const i of instruments) {
      const bucket = stats[i.section];
      if (!bucket) continue;
      bucket.total += 1;
      if (completed[i.instrument_id]) bucket.done += 1;
    }
    return stats;
  }, [instruments, completed]);

  const inst = sectionInstruments[instIdx];
  const item = inst?.items?.[qIdx];
  const instKey = inst?.instrument_id;
  const instResponses = responses[instKey] || {};
  const totalItems = inst?.items?.length || 0;
  const progress = totalItems ? Math.round(((qIdx + (instResponses[item?.item_id] !== undefined ? 1 : 0)) / totalItems) * 100) : 0;

  const enterSection = (sectionKey) => {
    setActiveSection(sectionKey);
    setInstIdx(0);
    setQIdx(0);
  };
  const backToPicker = () => {
    setActiveSection(null);
    setInstIdx(0);
    setQIdx(0);
  };

  const setAnswer = (val) => {
    setResponses((r) => ({
      ...r,
      [instKey]: { ...(r[instKey] || {}), [item.item_id]: parseInt(val, 10) },
    }));
  };

  const next = async () => {
    if (instResponses[item.item_id] === undefined) {
      toast.error("Please choose a response");
      return;
    }
    if (qIdx < totalItems - 1) {
      setQIdx((i) => i + 1);
      return;
    }
    // Submit
    setSubmitting(true);
    try {
      const submitRes = await api.post("/assessments/submit", {
        instrument_id: instKey,
        responses: instResponses,
      });
      setCompleted((c) => ({ ...c, [instKey]: true }));
      toast.success(`${inst.title} submitted`);
      // `score` on the response is the full scoring object (see
      // score_instrument): {score, band, label, total_max, domains?}.
      const s = submitRes?.data?.score || {};
      setJustCompleted({
        instrument_id: instKey,
        title: inst.title,
        section: inst.section,
        score: s.score ?? s.total,
        band: s.band,
        label: s.label,
      });
      // Section-completion celebration still fires when the LAST unfinished
      // instrument in a section just went complete.
      const sectionRemaining = sectionInstruments.filter(
        (i) => i.instrument_id !== instKey && !completed[i.instrument_id]
      ).length;
      if (sectionRemaining === 0) {
        setCelebrating(activeSection);
      } else {
        // Return to picker so the user can pick their next questionnaire.
        backToPicker();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const prev = () => {
    if (qIdx > 0) setQIdx((i) => i - 1);
    else if (instIdx > 0) {
      const prevInst = sectionInstruments[instIdx - 1];
      setInstIdx((i) => i - 1);
      setQIdx((prevInst?.items?.length || 1) - 1);
    }
  };

  const [pushing, setPushing] = useState(false);
  const pushSnapshot = async () => {
    // Bundle every persisted answer + voice metadata + demographics (server-side,
    // no personal name) with whatever the respondent has just filled in-memory.
    setPushing(true);
    try {
      const token = localStorage.getItem("mano_jwt");
      const resp = await fetch(`${API}/assessments/my/export`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const server = await resp.json();
      // In-progress (unsaved) answers keyed by instrument_id → item_id → value.
      // Include instrument titles + sections so the file is self-describing.
      const inProgress = Object.entries(responses).map(([instId, answers]) => {
        const meta = instruments.find((i) => i.instrument_id === instId);
        return {
          instrument_id: instId,
          instrument_title: meta?.title || instId,
          section: meta?.section || null,
          answered_count: Object.keys(answers || {}).length,
          total_items: meta?.items?.length || null,
          answers,
        };
      });
      const bundle = {
        ...server,
        in_progress: {
          captured_at: new Date().toISOString(),
          current_instrument: inst?.instrument_id || null,
          current_question_index: qIdx,
          instruments: inProgress,
        },
      };
      const blob = new Blob([JSON.stringify(bundle, null, 2)],
                            { type: "application/json" });
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      const filename = `manodayam-my-snapshot-${stamp}.json`;
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(href), 1500);
      toast.success(`Snapshot pushed — ${filename}`);
    } catch (e) {
      toast.error(e?.message?.includes("HTTP")
        ? "Could not build snapshot right now"
        : "Push failed — please retry");
    } finally {
      setPushing(false);
    }
  };

  if (!instruments.length) {
    return (
      <div className="min-h-screen bg-background">
        <BrandHeader />
        <div className="mano-container-narrow py-20 text-muted-foreground">Loading questionnaires…</div>
      </div>
    );
  }

  // -------- Section picker mode (default landing) --------
  if (!activeSection) {
    return (
      <div className="min-h-screen bg-background">
        <BrandHeader />
        <main className="mano-container-narrow py-12">
          <ProgressStepper steps={RESPONDENT_STEPS} current="questionnaires" />

          <div className="mb-8 mt-2 flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="font-display font-semibold text-2xl sm:text-3xl tracking-tight"
                  data-testid="section-picker-title">
                Pick a section to work on
              </h1>
              <p className="text-muted-foreground mt-2 leading-relaxed">
                Complete the sections in any order — your progress on each group
                is saved as you go.
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={() => setChangePwOpen(true)}
                    className="rounded-full"
                    data-testid="picker-change-password-btn">
              <KeyRound size={13} /> Change password
            </Button>
          </div>

          {justCompleted && (
            <div className="mb-6 rounded-2xl border border-primary/40 bg-primary/5 p-5 md:p-6 flex flex-wrap items-center justify-between gap-4"
                 data-testid="picker-just-completed">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-primary flex items-center gap-1">
                  <CheckCircle2 size={11} /> Just submitted
                </div>
                <div className="font-display font-semibold text-xl mt-1 truncate"
                     data-testid="picker-just-completed-title">
                  {justCompleted.title}
                </div>
                {justCompleted.label && (
                  <div className="text-sm text-muted-foreground mt-1">{justCompleted.label}</div>
                )}
              </div>
              <div className="flex items-center gap-4">
                {justCompleted.band && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-xs font-medium text-primary"
                        data-testid="picker-just-completed-band">
                    {justCompleted.band}
                  </span>
                )}
                {justCompleted.score !== undefined && justCompleted.score !== null && (
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Score</div>
                    <div className="font-display text-2xl leading-none" data-testid="picker-just-completed-score">
                      {justCompleted.score}
                    </div>
                  </div>
                )}
                <Button variant="ghost" size="sm" onClick={() => setJustCompleted(null)}
                        className="rounded-full text-muted-foreground"
                        data-testid="picker-dismiss-just-completed">
                  Dismiss
                </Button>
              </div>
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="section-picker">
            {SECTION_ORDER.map((key) => {
              const meta = SECTION_META[key];
              const stats = sectionStats[key] || { total: 0, done: 0 };
              const pct = stats.total ? Math.round((stats.done / stats.total) * 100) : 0;
              const done = stats.done === stats.total && stats.total > 0;
              const Icon = meta.icon;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => enterSection(key)}
                  disabled={stats.total === 0}
                  data-testid={`section-card-${key}`}
                  className="text-left group rounded-2xl border border-border/70 bg-card p-6 mano-lift transition-shadow hover:shadow-md disabled:opacity-50"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                      <Icon size={22} />
                    </div>
                    {done && (
                      <span className="inline-flex items-center gap-1 text-xs text-primary"
                            data-testid={`section-card-done-${key}`}>
                        <CheckCircle2 size={12} /> Complete
                      </span>
                    )}
                  </div>
                  <div className="font-display font-semibold text-xl mt-4">{meta.label}</div>
                  <div className="text-xs text-muted-foreground mt-1">{meta.tagline}</div>

                  <div className="mt-5 flex items-center justify-between text-xs">
                    <span className="text-muted-foreground" data-testid={`section-card-count-${key}`}>
                      {stats.done} of {stats.total} complete
                    </span>
                    <span className="text-muted-foreground">{pct}%</span>
                  </div>
                  <div className="mt-2 h-1.5 w-full rounded-full bg-muted overflow-hidden">
                    <div className={"h-1.5 rounded-full transition-[width] duration-300 " +
                                    (done ? "bg-primary" : "bg-secondary")}
                         style={{ width: `${Math.max(pct, stats.total ? 4 : 0)}%` }} />
                  </div>

                  <div className="mt-5 inline-flex items-center gap-1 text-sm text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                    {done ? "Review answers" : stats.done ? "Continue section" : "Start section"}
                    <ArrowRight size={14} />
                  </div>
                </button>
              );
            })}
          </div>

          <div className="mt-6 flex justify-end">
            <Button variant="outline" onClick={() => nav("/results")}
                    className="rounded-full px-6"
                    data-testid="picker-view-results-btn">
              View all my results
            </Button>
          </div>
        </main>
        <SectionCelebration
          sectionKey={celebrating}
          sectionStats={sectionStats}
          onClose={() => setCelebrating(null)}
          onContinue={() => {
            setCelebrating(null);
            // Finale → main hub (/questionnaires picker with everything
            // visible). Non-finale keeps the old jump-to-results behaviour.
            const remaining = SECTION_ORDER.filter((k) => {
              if (k === celebrating) return false;
              const s = sectionStats[k] || { total: 0, done: 0 };
              return s.total > 0 && s.done < s.total;
            });
            if (remaining.length === 0) { backToPicker(); }
            else { nav("/results"); }
          }}
          onPickAnother={() => { setCelebrating(null); backToPicker(); }}
        />
        <ChangePasswordDialog open={changePwOpen} onOpenChange={setChangePwOpen} />
      </div>
    );
  }

  if (!inst) {
    // Should not happen — safety net.
    return (
      <div className="min-h-screen bg-background">
        <BrandHeader />
        <div className="mano-container-narrow py-20 text-muted-foreground">
          No questionnaires in this section.
          <Button variant="outline" onClick={backToPicker} className="ml-3">Back to sections</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <BrandHeader />
      <main className="mano-container-narrow py-12">
        <ProgressStepper steps={RESPONDENT_STEPS} current="questionnaires" />

        <div className="mb-4">
          <Button variant="ghost" size="sm" onClick={backToPicker}
                  className="text-muted-foreground hover:text-foreground -ml-2"
                  data-testid="questionnaire-back-to-sections">
            <ArrowLeft size={14} /> Back to sections
          </Button>
        </div>

        <div className="mb-6">
          <div className="flex items-center gap-3 text-xs uppercase tracking-wider text-muted-foreground">
            <span data-testid="questionnaire-section">{(SECTION_META[inst.section]?.label) || inst.section.replace(/_/g, " ")}</span>
            <span>·</span>
            <span>Instrument {instIdx + 1} of {sectionInstruments.length}</span>
          </div>
          <h1 className="font-display font-semibold text-2xl sm:text-3xl tracking-tight mt-2" data-testid="questionnaire-title">
            {inst.title}
          </h1>
          {inst.description && (
            <p className="text-muted-foreground mt-2 leading-relaxed">{inst.description}</p>
          )}
        </div>

        <div className="h-1.5 w-full rounded-full bg-muted mb-8" aria-label="Progress" data-testid="questionnaire-progress">
          <div className="h-1.5 rounded-full bg-secondary transition-[width] duration-300" style={{ width: `${progress}%` }} />
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <Card className="mano-card">
          <CardContent className="p-6 md:p-10">
            <div className="text-xs text-muted-foreground mb-3">
              Question {qIdx + 1} of {totalItems}
            </div>
            <p className="font-display text-xl sm:text-2xl leading-relaxed" data-testid="questionnaire-question">
              {item.text}
            </p>

            <RadioGroup
              value={instResponses[item.item_id]?.toString() ?? ""}
              onValueChange={setAnswer}
              className="mt-8 grid gap-3"
            >
              {(item.response_scale || inst.response_scale).map((opt) => {
                const active = instResponses[item.item_id] === opt.value;
                return (
                  <label
                    key={opt.value}
                    htmlFor={`opt-${opt.value}`}
                    className={
                      "flex items-center gap-3 rounded-lg border p-4 cursor-pointer transition-colors duration-150 " +
                      (active
                        ? "border-primary bg-secondary/25"
                        : "border-border hover:bg-muted/50")
                    }
                    data-testid={`questionnaire-option-${opt.value}`}
                  >
                    <RadioGroupItem id={`opt-${opt.value}`} value={opt.value.toString()} />
                    <span className="text-base">{opt.label}</span>
                  </label>
                );
              })}
            </RadioGroup>

            <div className="mt-10 flex flex-wrap items-center justify-between gap-3">
              <Button
                variant="outline"
                onClick={prev}
                disabled={qIdx === 0 && instIdx === 0}
                className="rounded-full px-6"
                data-testid="questionnaire-prev"
              >
                Previous
              </Button>
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  variant="secondary"
                  onClick={pushSnapshot}
                  disabled={pushing}
                  className="rounded-full px-5"
                  data-testid="questionnaire-push-btn"
                  title="Download a JSON snapshot of every answer, voice sample and profile detail (name is excluded)"
                >
                  <Download size={16} />
                  {pushing ? "Pushing…" : "Push snapshot"}
                </Button>
                <Button
                  onClick={next}
                  disabled={submitting}
                  className="rounded-full px-6 mano-lift"
                  data-testid="questionnaire-next"
                >
                  {qIdx === totalItems - 1
                    ? (instIdx === instruments.length - 1 ? "Finish" : "Submit & continue")
                    : "Next"}
                  <ArrowRight size={16} />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
        </div>
        <aside className="lg:col-span-1 space-y-2"
               data-testid="questionnaire-sidebar">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
            Other questionnaires in {(SECTION_META[inst.section]?.label) || "this section"}
          </div>
          {sectionInstruments.filter((i) => i.instrument_id !== inst.instrument_id).length === 0 && (
            <div className="rounded-lg border border-dashed border-border/60 p-4 text-xs text-muted-foreground"
                 data-testid="questionnaire-sidebar-empty">
              This section only has one questionnaire.
            </div>
          )}
          {sectionInstruments.filter((i) => i.instrument_id !== inst.instrument_id).map((si) => {
            const isDone = completed[si.instrument_id];
            return (
              <button key={si.instrument_id} type="button"
                      onClick={() => {
                        const idx = sectionInstruments.findIndex((i) => i.instrument_id === si.instrument_id);
                        if (idx >= 0) { setInstIdx(idx); setQIdx(0); }
                        // `responses` is keyed by instrument_id — the user's
                        // in-progress answers for the current instrument are
                        // preserved and restored automatically on switch back.
                      }}
                      data-testid={`sidebar-instrument-${si.instrument_id}`}
                      className={"w-full text-left rounded-lg border p-3 text-sm transition-colors mano-lift " +
                                  (isDone ? "border-primary/40 bg-primary/5 hover:bg-primary/10"
                                          : "border-border/70 hover:bg-muted/40")}>
                <div className="flex items-center justify-between gap-2">
                  <div className="font-medium truncate">{si.title}</div>
                  {isDone && <CheckCircle2 size={13} className="text-primary shrink-0" />}
                </div>
                <div className="text-[10px] text-muted-foreground mt-0.5">
                  {(si.items || []).length} question{(si.items || []).length === 1 ? "" : "s"}
                  {isDone ? " · Done" : ""}
                </div>
              </button>
            );
          })}
          <div className="pt-3 border-t border-border/40 mt-3">
            <Button variant="ghost" size="sm" onClick={backToPicker}
                    className="rounded-full text-xs text-muted-foreground w-full justify-start"
                    data-testid="sidebar-back-to-sections">
              <ArrowLeft size={12} /> Back to all sections
            </Button>
          </div>
        </aside>
        </div>

        <div className="mt-6 flex flex-wrap gap-2 text-xs text-muted-foreground" data-testid="questionnaire-list">
          {instruments.map((i, idx) => (
            <span
              key={i.instrument_id}
              className={
                "inline-flex items-center gap-1 rounded-full px-3 py-1 border " +
                (completed[i.instrument_id]
                  ? "bg-primary/10 border-primary/40 text-primary"
                  : idx === instIdx
                  ? "border-secondary/70 bg-secondary/20"
                  : "border-border")
              }
            >
              {completed[i.instrument_id] && <CheckCircle2 size={12} />}
              {i.instrument_id}
            </span>
          ))}
        </div>
      </main>
    </div>
  );
}
